// CardDraggableList.tsx

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { fileUrl } from 'api/api';
import DrageMove from 'assets/icon/appointment/card/drag-move';
import StarActive from 'assets/icon/starActive';
import { colors } from 'constants/theme';
import { ManegeWebAction } from 'features/application/manage-web/manage-web';
import { StarStatusButton } from 'new-components/buttons/star-status-button';
import { ReactNode } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { dateToView } from 'utils/date.utils';

export const CardManageWeb = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 188,
    border: '1px solid #F3F1F1',
    padding: 12,
    marginBottom: 12,
    backgroundColor: '#fff',
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
    position: 'relative',

    '.content-card': {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',

        '.container-img': {
            width: 140,
            height: 140,
            borderRadius: 30,
            boxShadow: '1px 1px 6px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            img: {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
            },
        },

        '.container-des': {
            position: 'relative',
            marginLeft: 12,
            paddingRight: 8,
            height: '100%',
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',  // <-- เปลี่ยนจาก space-between มาเป็น flex-start

            '.content-des-text': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                hyphens: 'auto',
            },
        },
    },

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        height: 'auto',
        padding: theme.spacing(2),

        '.content-card': {
            flexDirection: 'column',
            alignItems: 'flex-start',

            '.container-img': {
                width: '100%',
                position: 'relative',
                pt: '100%', // padding-top:100% for 1:1 aspect ratio
                borderRadius: theme.shape.borderRadius,
                marginBottom: theme.spacing(2),
                overflow: 'hidden',
                img: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                },
            },

            '.container-des': {
                marginLeft: 0,
                paddingRight: 0,
                width: '100%',
            },
        },
    },
}));

const renderStarScore = (score: number) =>
    Array(5)
        .fill(null)
        .map((_, i) => (
            <StarActive
                key={i}
                stroke="#F6E331"
                fill={i < score ? '#F6E331' : 'transparent'}
            />
        ));

interface CardDraggableListProps {
    data: any[];
    isDragging: boolean;
    onDragEnd: (r: any) => void;
    onDragStart: (r: any) => void;
    onActive: (item: any) => void;
    onInActive: (item: any) => void;
    onEdit: (status: boolean, item: any) => void;
    onDelete: (item: any) => void;
    idField: string;
    topicField: string;
    children?: ReactNode;
}

const CardDraggableList = ({
    data,
    isDragging,
    onDragEnd,
    onDragStart,
    onActive,
    onInActive,
    onEdit,
    onDelete,
    idField,
    topicField,
}: CardDraggableListProps) => (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <Box sx={{ pr: { xs: 0, md: '16px' }, pb: isDragging ? '200px' : 0 }}>
            <Droppable droppableId={topicField}>
                {(dropProvided) => (
                    <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                        {data.map((item, index) => (
                            <Draggable
                                key={item[idField]}
                                draggableId={`${item[idField]}`}
                                index={index}
                            >
                                {(dragProvided, dragSnapshot) => (
                                    <CardManageWeb
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                    >
                                        <Box className="content-card">
                                            <Box className="container-img">
                                                <img
                                                    src={`${fileUrl}/${item.filePath}/${item.fileName}`}
                                                    alt="article"
                                                />
                                            </Box>

                                            <Box className="container-des">
                                                {/* หัวเรื่อง */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            noWrap
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: { xs: 14, sm: 18 },
                                                            }}
                                                        >
                                                            {item.topic}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', ml: 1 }}
                                                    >
                                                        <Box sx={{ cursor: 'pointer' }}>
                                                            <DrageMove
                                                                fill={
                                                                    dragSnapshot.isDragging
                                                                        ? colors.themeSecondColor
                                                                        : undefined
                                                                }
                                                                width="20px"
                                                                height="20px"
                                                            />
                                                        </Box>
                                                        <Box sx={{ ml: 1, cursor: 'pointer' }}>
                                                            <ManegeWebAction
                                                                onActive={() => onActive(item)}
                                                                onInActive={() => onInActive(item)}
                                                                disabledActive={item.status === 'ACTIVE'}
                                                                onEdit={() => onEdit(true, item)}
                                                                onDelete={() => onDelete(item)}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* ย่อความใต้หัวเรื่อง (subTitle) */}
                                                <Box
                                                    sx={{
                                                        mt: { xs: 0.5, sm: 0.5 },
                                                        mb: { xs: 1, sm: 1 },
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            fontSize: { xs: 12, sm: 14 },
                                                        }}
                                                    >
                                                        {item.subTopic}
                                                    </Typography>
                                                </Box>

                                                {/* แถวดาว */}
                                                {topicField === 'review' && (<Box
                                                    sx={{
                                                        display: 'flex',
                                                        mt: { xs: 1, sm: 5, md: 6, xl: 6 },
                                                        gap: { xs: 0.5, sm: 1 },
                                                        '& svg': {
                                                            width: { xs: 14, sm: 20 },
                                                            height: { xs: 14, sm: 20 },
                                                        },
                                                    }}
                                                >
                                                    {renderStarScore(item.score || 0)}
                                                </Box>)}

                                                {/* แถวล่างสุด: สถานะ | ผู้แก้ไข-วันที่ */}
                                                <Box
                                                    sx={{
                                                        mt: 'auto',    // ดันขึ้นล่างสุด
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%',

                                                    }}
                                                >
                                                    <StarStatusButton status={item.status} />
                                                    {topicField === 'article' && (<Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            fontSize: { xs: 12, sm: 14 },
                                                            color: '#535962',
                                                        }}
                                                    >
                                                        <Typography noWrap>
                                                            {item.updatedBy || item.createdBy}
                                                        </Typography>
                                                        <Typography noWrap>
                                                            {dateToView(item.updatedAt) ||
                                                                dateToView(item.createdAt)}
                                                        </Typography>
                                                    </Box>)}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardManageWeb>
                                )}
                            </Draggable>
                        ))}
                        {dropProvided.placeholder}
                    </Box>
                )}
            </Droppable>
        </Box>
    </DragDropContext>
);

export default CardDraggableList;
